// @flow

import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import * as Yup from 'yup';
import { i18n, Analytics } from 'shared/utils';
import UpdateRunSetupStartMutation from 'shared/mutations/UpdateRunSetupStart';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import { Formik, Form, FieldGroup } from 'shared/components/form';
import FatButton from 'shared/components/common/FatButton';
import type { SetupStartModal_run as RunFragment } from './__generated__/SetupStartModal_run';

type Props = {
  onClose: () => void,
  run: RunFragment,
};

const LANGUAGE = {
  resume: {
    title: i18n.t('Resume Setup?'),
    button: i18n.t('Resume Setup'),
    success: i18n.t('Setup Resumed'),
  },
  start: {
    title: i18n.t('Start Setup?'),
    button: i18n.t('Start Setup'),
    success: i18n.t('Setup Started'),
  },
};

const SetupStartModal = ({ onClose, run }: Props) => {
  const language = LANGUAGE[run.status === 'PAUSED' ? 'resume' : 'start'];

  return (
    <Modal maxWidth={700}>
      <Formik
        initialValues={{
          userNumber: '',
          notes: '',
        }}
        validationSchema={Yup.object().shape({
          userNumber: Yup.string().required('Required'),
          notes: Yup.string(),
        })}
        onSubmit={async (values: *, { setSubmitting }: *) => {
          setSubmitting(true);

          try {
            await UpdateRunSetupStartMutation.commit({
              variables: {
                input: {
                  runId: run.id,
                  userNumber: values.userNumber,
                  notes: values.notes,
                },
              },
            });

            onClose();
            Analytics.trackEvent('Setup Start', {
              operatorID: values.userNumber,
              notes: values.notes,
              machineType: run.machine?.type?.name,
            });
          } catch (e) {
            setSubmitting(false);
            window.alert(e.message);
          }
        }}
        render={({ values, errors, isValid, isSubmitting, handleSubmit }) => (
          <Form>
            <ModalHeader header={language.title} onClose={onClose} />
            <ModalBody withPadding>
              <FieldGroup
                name="userNumber"
                type="number"
                label={i18n.t('Operator ID')}
                placeholder={i18n.t('Enter ID')}
                error={errors.userNumber}
              />
              <FieldGroup
                name="notes"
                type="textarea"
                label={i18n.t('Notes')}
                placeholder={i18n.t('Type here...')}
                error={errors.notes}
                height={80}
              />
            </ModalBody>
            <ModalFooter style={{ display: 'block', textAlign: 'center' }}>
              <FatButton
                type="submit"
                theme="blue"
                disabled={!isValid}
                loading={isSubmitting}
                onClick={handleSubmit}
              >
                {language.button}
              </FatButton>
            </ModalFooter>
          </Form>
        )}
      />
    </Modal>
  );
};

export default createFragmentContainer(SetupStartModal, {
  run: graphql`
    fragment SetupStartModal_run on Run {
      id
      status
      machine {
        id
        type {
          id
          name
        }
      }
    }
  `,
});
